import { Box, Collapse, Flex, Grid, Text } from '@chakra-ui/react'
import { FC, useEffect, useMemo } from 'react'

import useKpiSales from '@hook/kpi/use-sales'
import useFilters from '@hook/shared/use-filters'
import useLayout from '@hook/shared/use-layout'
import useSessions from '@hook/shared/use-sessions'

import Layout from '@component/Layout'
import LayoutEmpty from '@component/Layout/Empty'
import LayoutFilters from '@component/Layout/Filters'
import LayoutLoader from '@component/Layout/Loader'
import TableGrid from '@component/Layout/TableGrid'
import LayoutTools from '@component/Layout/Tools'
import KpiSalesGraphic from '@component/Modules/Kpi/Graphics/KpiSalesGraphic'
import OpenOrCloseChartButton from '@component/Modules/Kpi/Graphics/OpenOrCloseButton'

import { disableActionsByDefault } from '@helper/kpi/sales/disableActionsByDefault'
import { scalesHeightKpiTable } from '@helper/kpi/sales/scalesHeightKpiTable'
import { validateAppVersion } from '@helper/util/storangeClear'
import generic from '@service/generic'

const KpiSales: FC = () => {
  const { hasSessions } = useSessions()
  const { getFilters } = useFilters()
  const { menuToggle } = useLayout()
  const {
    data,
    query,
    params,
    isModal,
    actions,
    headers,
    handleLocalParams,
    filters,
    loading,
    handlePageChange,
    filename,
    selected,
    fetching,
    isGraphic,
    filtersList,
    currentyDate,
    loadingChart,
    graphicValues,
    tableInteraction,
    selectRealOrBtleo,
    isShowContentChart,
    getData,
    pagination,
    setQuery,
    setFilters,
    setFilename,
    exportKpiData,
    clearAllFilters,
    updateIsGraphic,
    handleUpdateParams,
    updateIsModalConfig,
    handleGraphicIsVisible,
    parseParamsModalOrGraphic,
    handleSetSelectRealOrBtleo
  } = useKpiSales()

  const height = useMemo(
    () =>
      menuToggle
        ? hasSessions
          ? 'calc(100vh - 30px)'
          : '100vh'
        : hasSessions
        ? 'calc(100vh - 160px)'
        : 'calc(100vh - 142px)',
    [menuToggle, hasSessions]
  )

  const tableHeight = useMemo(
    () => scalesHeightKpiTable({ menuToggle, isGraphic, hasSessions }),
    [menuToggle, isGraphic, hasSessions]
  )

  useEffect(() => {
    handleLocalParams()
    generic
      .getMaintence('manutencao')
      .then(({ data: { data } }) => {
        validateAppVersion(data.version)

        if (data.valor === 'true') {
          window.location.href = '/manutencao'
        } else {
          data?.length === 0 && getData()

          getFilters({
            companies: true,
            centers: true,
            categoryManagers: true,
            productManagers: true,
            worlds: true,
            compactCategories: true,
            categories: true,
            inventoryStatus: true,
            kpiStatus: true,
            abcCurves: true,
            providersForDistribution: true,
            subCategories: true
          })
        }
      })
      .catch(() => {})

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <Layout title="KPI Vendas">
      <Grid
        w="100vw"
        bg="white"
        h={height}
        templateColumns={!filters ? '100vw' : '260px calc(100vw - 260px)'}
        isDisabled={loading || fetching || data.length === 0}
      >
        {filters && (
          <LayoutFilters
            filters={filtersList}
            values={params}
            defaultDate={currentyDate}
            onChange={handleUpdateParams}
            loading={loading}
            canCancel={fetching}
            onFilter={getData}
            onClearAllFilters={clearAllFilters}
          />
        )}
        <Box
          overflow={
            isShowContentChart && graphicValues.margem?.length > 0
              ? 'auto'
              : null
          }
          maxH={
            isShowContentChart && graphicValues.margem?.length > 0
              ? 'calc(100vh - 136px)'
              : null
          }
        >
          <LayoutTools
            title={filename}
            filters={filters}
            actions={actions}
            query={query}
            onClick={exportKpiData}
            onSearchChange={setQuery}
            onTitleChange={setFilename}
            toggleFilters={setFilters.toggle}
            isDisabled={loading || fetching || data.length === 0}
            disableActionsByDefault={disableActionsByDefault}
          />
          {loading ? (
            <LayoutLoader
              title="Recuperando informações..."
              message="Aguarde enquanto buscamos os registros no banco de dados"
            />
          ) : data.length === 0 ? (
            <LayoutEmpty
              title="Nada encontrado!"
              message="Filtre sua busca ao lado para obter informações"
            />
          ) : (
            <>
              <Flex
                justifyContent="space-between"
                alignItems="center"
                paddingRight={4}
                paddingLeft={3}
                paddingY={1}
              >
                <Text fontSize={14}>
                  Quantidade de Registros: {pagination.totalRows}
                </Text>

                {isShowContentChart && graphicValues.margem?.length > 0 && (
                  <OpenOrCloseChartButton
                    openText="Abrir gráficos"
                    closeText="Fechar gráficos"
                    graphicValue={graphicValues.vendas}
                    isGraphic={isGraphic}
                    setIsGraphic={() => handleGraphicIsVisible(!isGraphic)}
                  />
                )}
              </Flex>

              {isShowContentChart && (
                <Collapse
                  in={
                    loadingChart ||
                    (!loadingChart &&
                      graphicValues.margem?.length > 0 &&
                      isGraphic)
                  }
                  unmountOnExit={true}
                >
                  {loadingChart ? (
                    <LayoutLoader
                      type="chart"
                      title="Recuperando informações..."
                      message="Aguarde enquanto buscamos os registros no banco de dados"
                    />
                  ) : graphicValues.margem?.length === 0 ? (
                    <LayoutEmpty
                      type="chart"
                      title="Nada encontrado!"
                      message="Filtre sua busca ao lado para obter informações"
                    />
                  ) : (
                    isGraphic && (
                      <>
                        <KpiSalesGraphic
                          getChartData={parseParamsModalOrGraphic}
                          updateRealOrBtleoState={handleSetSelectRealOrBtleo}
                          realOrBtleoState={selectRealOrBtleo}
                          data={graphicValues}
                        />
                        <Box
                          paddingBottom={3.5}
                          boxShadow="inset 0px 1px 5px 2px #dcdcdc"
                        />
                      </>
                    )
                  )}
                </Collapse>
              )}

              <TableGrid
                id="sales-table"
                entity="kpi-sales"
                headers={headers}
                query={query}
                data={data}
                loading={fetching}
                height={tableHeight}
                actions={tableInteraction}
                itsSortable={true}
                isModal={isModal}
                selected={selected}
                updateIsGraphic={updateIsGraphic}
                updateIsModalConfig={updateIsModalConfig}
                pagination={pagination}
                udatePageChange={handlePageChange}
                isPagedBack={true}
                isPaged={false}
                isOverflowDisabled={true}
              />
            </>
          )}
        </Box>
      </Grid>
    </Layout>
  )
}

export default KpiSales
